import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "@anchor/react-components/dist/lib/components";
import Fuse from "fuse.js";
import { useQuery } from "react-query";
import { PostmortemServices } from "../../services/postmortemServices";
import UserInfo from "../../context/UserAccessContext";
import "./GlobalSearch.css";

const GlobalSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { fetchAllPostmortems } = PostmortemServices();
  const { userIsAdmin, userAccessData, graphData } = UserInfo();

  // Use React Query for GlobalSearch - separate cache from NavBar dropdown
  const { data: allPostmortems = [] } = useQuery(
    ["globalSearchData", graphData, userAccessData, userIsAdmin],
    () => fetchAllPostmortems(userIsAdmin, userAccessData, "search"),
    {
      enabled: isOpen && !!userAccessData && !!graphData,
      refetchOnWindowFocus: false,
      staleTime: 3 * 60 * 1000, // 3 minutes - full data for comprehensive search
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fuse.js configuration for fuzzy search
  const fuseOptions = {
    keys: [
      { name: "customID", weight: 1.0 },          // Primary postmortem ID - highest priority
      { name: "incidentID", weight: 0.95 },       // ServiceNow incident ID - also high priority
      { name: "rootCause.rootCauseCode", weight: 0.9 },
      { name: "incidentSummary.summary", weight: 0.8 },
      { name: "rootCause.rootCauseSubCode", weight: 0.8 },
      { name: "businessImpact.description", weight: 0.7 },
      { name: "rootCause.cause", weight: 0.6 },
      { name: "owningBusinessUnitUser", weight: 0.6 },
      { name: "precedingEvents.description", weight: 0.5 },
      { name: "problemID", weight: 0.5 },
      { name: "detectionResponse.description", weight: 0.4 },
      { name: "recovery.recovery", weight: 0.4 },
      { name: "application", weight: 0.4 },
      { name: "portfolio", weight: 0.3 },
      { name: "priority", weight: 0.3 },
      { name: "status", weight: 0.2 },
      { name: "platform", weight: 0.2 },
      { name: "product", weight: 0.1 },
    ],
    includeScore: true,
    threshold: 0.4,
    minMatchCharLength: 2,
    includeMatches: true,
    findAllMatches: true,
  };

  const fuse = useMemo(() => {
    return allPostmortems.length > 0
      ? new Fuse(allPostmortems, fuseOptions)
      : null;
  }, [allPostmortems]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Perform search when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery.trim() || !fuse) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }

      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }

      setIsLoading(true);
      setHasSearched(true);

      // Simulate search delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 200));

      try {
        const results = fuse.search(searchQuery);
        const formattedResults = results.slice(0, 10).map((result) => ({
          ...result.item,
          score: result.score,
          matches: result.matches,
        }));

        setSearchResults(formattedResults);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, fuse]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  const handleClose = () => {
    setSearchQuery("");
    setSearchResults([]);
    setHasSearched(false);
    onClose();
  };

  const handleResultClick = (postmortem) => {
    const isArchived = postmortem.state === "DONE";
    const baseUrl = isArchived
      ? "/archived-postmortems"
      : "/ongoing-postmortems";

    handleClose();

    navigate(baseUrl, {
      state: { incidentID: postmortem.customID, highlightRow: true },
    });
  };

  const handleEditClick = (postmortem, event) => {
    event.stopPropagation();
    handleClose();
    navigate(
      `/editpostmortem?postmortemID=${postmortem.customID}&page_type=edit`
    );
  };

  const handleReportClick = (postmortem, event) => {
    event.stopPropagation();
    handleClose();
    const incidentIDParam = postmortem.incidentID ? encodeURIComponent(postmortem.incidentID) : "";
    navigate(
      `/report?incidentID=${incidentIDParam}&postmortemID=${encodeURIComponent(postmortem.customID)}&page_type=report`
    );
  };

  const getMatchingFields = (matches) => {
    if (!matches || matches.length === 0) return [];

    const fieldHierarchy = {
      customID: "Postmortem ID",
      incidentID: "Incident ID",
      summary: "Incident Summary → Summary",
      description: (key) => {
        if (key.includes("businessImpact"))
          return "Business Impact → Description";
        if (key.includes("rootCause")) return "Root Cause → Cause";
        if (key.includes("precedingEvents"))
          return "Preceding Events → Description";
        if (key.includes("detectionResponse"))
          return "Detection Response → Description";
        return "Description";
      },
      recovery: "Recovery → Recovery Details",
      priority: "Priority",
      status: "Status",
      product: "Product",
      platform: "Platform",
      portfolio: "Portfolio",
      application: "Application",
      "rootCause.rootCauseCode": "Root Cause → Root Cause Code",
      "rootCause.rootCauseSubCode": "Root Cause → Root Cause Sub Code",
      owningBusinessUnitUser: "Owning Business Unit/User",
      problemID: "Problem ID",
    };

    return matches
      .filter((match) => match.value && match.value.trim().length > 0)
      .map((match) => {
        const fullKey = match.key;
        const fieldName = fullKey.split(".").pop();

        let displayName;
        if (typeof fieldHierarchy[fieldName] === "function") {
          displayName = fieldHierarchy[fieldName](fullKey);
        } else {
          displayName = fieldHierarchy[fieldName] || fieldName;
        }

        return {
          field: displayName,
          value: match.value,
          indices: match.indices,
          originalKey: fullKey,
        };
      })
      .slice(0, 2);
  };

  const highlightMatch = (text, indices) => {
    if (!indices || indices.length === 0) return text;

    let result = [];
    let lastIndex = 0;

    indices.forEach(([start, end]) => {
      result.push(text.substring(lastIndex, start));
      result.push(
        <mark key={`${start}-${end}`} className="search-highlight">
          {text.substring(start, end + 1)}
        </mark>
      );
      lastIndex = end + 1;
    });

    result.push(text.substring(lastIndex));
    return result;
  };

  const getStatusColor = (state) => {
    switch (state) {
      case "DONE":
        return "#28a745";
      case "IN_PROGRESS":
        return "#ffc107";
      case "DRAFT":
        return "#6c757d";
      default:
        return "#42B0D5";
    }
  };

  const getStatusText = (state) => {
    switch (state) {
      case "DONE":
        return "Archived";
      case "IN_PROGRESS":
        return "In Progress";
      case "DRAFT":
        return "Draft";
      default:
        return state;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="search-overlay" onClick={handleClose}>
      <div className="search-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="search-header">
          <div className="search-title">
            <Typography
              variant="h3"
              style={{ color: "#42B0D5", fontWeight: "700" }}
            >
              Search Postmortems
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#6c757d", marginTop: "4px" }}
            >
              Search across all incidents, summaries, and descriptions
            </Typography>
          </div>
          <button className="search-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        {/* Search Input */}
        <div className="search-input-container">
          <div className="search-input-wrapper">
            <div className="search-icon">
              {isLoading ? (
                <div className="search-spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19S2 15.194 2 10.5 5.806 2 10.5 2 19 5.806 19 10.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>

            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search postmortems, incident IDs, descriptions, root causes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />

            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="search-clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        <div className="search-results-container">
          {isLoading ? (
            <div className="search-loading">
              <div className="search-spinner large" />
              <Typography variant="h5" style={{ marginTop: "16px" }}>
                Searching...
              </Typography>
              <Typography variant="body2" style={{ color: "#6c757d" }}>
                Finding relevant postmortems...
              </Typography>
            </div>
          ) : searchResults.length > 0 ? (
            <>
              <div className="search-results-header">
                <Typography variant="body1" style={{ fontWeight: "600" }}>
                  Found {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""}
                </Typography>
              </div>
              <div className="search-results-list">
                {searchResults.map((result, index) => {
                  const matchingFields = getMatchingFields(result.matches);
                  return (
                    <div
                      key={result.incidentID || index}
                      className="search-result-item"
                      //   onClick={() => handleResultClick(result)}
                    >
                      <div className="result-header">
                        <div className="result-id">
                          <strong>{result.incidentID}</strong>
                        </div>
                        <div
                          className="result-status"
                          style={{
                            backgroundColor: getStatusColor(result.state),
                          }}
                        >
                          {getStatusText(result.state)}
                        </div>
                      </div>

                      {result.incidentSummary?.summary && (
                        <div className="result-summary">
                          {result.incidentSummary.summary.substring(0, 150)}
                          {result.incidentSummary.summary.length > 150
                            ? "..."
                            : ""}
                        </div>
                      )}

                      {matchingFields.length > 0 && (
                        <div className="result-matches">
                          {matchingFields.map((match, idx) => (
                            <div key={idx} className="match-field">
                              <span className="match-label">
                                {match.field}:
                              </span>
                              <span className="match-value">
                                {highlightMatch(
                                  match.value.substring(0, 100) +
                                    (match.value.length > 100 ? "..." : ""),
                                  match.indices
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="result-actions">
                        {result.state !== "DONE" && (
                          <button
                            className="action-button edit-button"
                            onClick={(e) => handleEditClick(result, e)}
                            title="Edit this postmortem"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="action-button report-button"
                          onClick={(e) => handleReportClick(result, e)}
                          title="View report"
                        >
                          Report
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : hasSearched ? (
            <div className="search-no-results">
              <Typography variant="h5">No results found</Typography>
              <Typography
                variant="body2"
                style={{ color: "#6c757d", marginTop: "8px" }}
              >
                Try different keywords or check your spelling
              </Typography>
            </div>
          ) : (
            <div className="search-empty-state">
              <Typography variant="h5">Start typing to search</Typography>
              <Typography
                variant="body2"
                style={{ color: "#6c757d", marginTop: "8px" }}
              >
                Search across all postmortem data including IDs, summaries, and
                descriptions
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
